import { environment } from '../../../environments/environment';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, switchMap, throwError, from } from 'rxjs';
import { refreshAuthToken, triggerSessionExpired } from './api-simulator.service';

/**
 * Interceptor HTTP para asegurar que todas las peticiones a la API:
 * 1. Envíen las cookies HttpOnly de sesión del usuario (`withCredentials: true`) y cabecera Authorization si existe token.
 * 2. Si alguna llamada responde con HTTP 401 Unauthorized:
 *    - Se invoca a POST /api/auth/refresh para verificar si se puede refrescar la sesión con cookies.
 *    - Si tiene éxito, se reintenta la llamada original.
 *    - Si falla el refresco, se ejecuta logout inmediato y se redirige a /login.
 */
export const credentialsInterceptor: HttpInterceptorFn = (req, next) => {
  let headers = req.headers;
  if (typeof localStorage !== 'undefined') {
    const token =
      localStorage.getItem('fm_real_api_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('jwt') ||
      localStorage.getItem('access_token');
    if (token && token !== 'cookie-session' && token.length > 5 && !headers.has('Authorization')) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const reqWithCredentials = req.clone({
    withCredentials: true,
    cache: 'no-store',
    headers,
  });

  return next(reqWithCredentials).pipe(
    catchError((error: HttpErrorResponse) => {
      if (environment.pwaEnabled && error.status === 0) {
        window.dispatchEvent(new Event('lom:connection-lost'));
      }
      // Ignorar si es login, logout o si es el propio refresh
      if (
        error.status === 401 &&
        !req.url.includes('/api/auth/login') &&
        !req.url.includes('/api/auth/refresh') &&
        !req.url.includes('/api/auth/logout')
      ) {
        return from(refreshAuthToken()).pipe(
          switchMap((refreshed) => {
            if (refreshed) {
              let retriedHeaders = req.headers;
              if (typeof localStorage !== 'undefined') {
                const refreshedToken =
                  localStorage.getItem('fm_real_api_token') ||
                  localStorage.getItem('token');
                if (refreshedToken && refreshedToken !== 'cookie-session' && refreshedToken.length > 5) {
                  retriedHeaders = retriedHeaders.set('Authorization', `Bearer ${refreshedToken}`);
                }
              }
              const retriedReq = req.clone({
                withCredentials: true,
                cache: 'no-store',
                headers: retriedHeaders,
              });
              return next(retriedReq);
            } else {
              triggerSessionExpired();
              return throwError(() => error);
            }
          })
        );
      }
      return throwError(() => error);
    })
  );
};
